import { Text, Paper, Title, Flex, Loader } from "@mantine/core";
import { createFileRoute, redirect } from "@tanstack/react-router";
import "@mantine/dates/styles.css";

import {
  getCalculationQueryOptions,
  Insurance,
} from "../../hooks/api/useGetCalculationById";
import { getTransferPolicyQueryOptions } from "../../hooks/api/useGetTransferPolicy";
import PensionOverviewStack from "../../components/pensionOverviewStack";
import { useUpdateCustomerData } from "../../hooks/ui";
import {
  isFullCredentials,
  getStoredCredentials,
} from "../../hooks/ui/customerDataStore";
import { PolicyRecommendations } from "../../components/policyRecommendations";
import { toFundSwitchPolicies } from "../../components/policyRecommendations/policies";
import { splitDashboardInsurances } from "../../utils/splitDashboardInsurances";
import { ManagedAllocationCard } from "../../components/managedAllocationCard";
import { FundSwitchAllocationCard } from "../../components/fundSwitchAllocationCard";
import { useLoaderData } from "@tanstack/react-router";

import PoaExpiredBanner from "../../components/poaValidation";
import ContactUsPaper from "../../components/contactUsPaper";
import { REPLAY_BLOCK_CLASS } from "../../utils/statsig";

export const Route = createFileRoute("/dashboard/")({
  component: Dashboard,
  loader: async ({ context }) => {
    const credentials = context.credentials?.sessionToken
      ? context.credentials
      : getStoredCredentials();

    if (!credentials?.sessionToken) {
      throw redirect({ to: "/login" });
    }

    if (isFullCredentials(credentials)) {
      const [data, transferPolicies] = await Promise.all([
        context.queryClient.ensureQueryData(
          getCalculationQueryOptions(credentials, "CUSTOMER_DASHBOARD"),
        ),
        context.queryClient.ensureQueryData(
          getTransferPolicyQueryOptions(credentials),
        ),
      ]);

      return {
        data,
        ...splitDashboardInsurances(
          data,
          Array.isArray(transferPolicies) ? transferPolicies : [],
        ),
      };
    }
    return {
      data: null,
      managedInsurances: [],
      otherInsurances: [],
      transferInsurances: [],
    };
  },
  pendingComponent: () => (
    <Flex justify={"center"} align={"center"} mih={"100vh"}>
      <Loader role="status" aria-label="Laddar innehåll" />
    </Flex>
  ),
});

function Dashboard() {
  const { customerData } = useUpdateCustomerData();
  const { data, managedInsurances, otherInsurances, transferInsurances } =
    useLoaderData({ from: "/dashboard/" });

  return (
    <>
      <PoaExpiredBanner />

      <Title ff={"var(--wallet-font-family-sambla-sans)"} mt={"xl"} order={1}>
        Hej{" "}
        <span className={REPLAY_BLOCK_CLASS}>
          {customerData?.firstName || "kära kund"}
        </span>
        !
      </Title>
      <Text mb={"xl"}>
        Vi har hämtat och sammanställt information om din pension så som den ser
        ut idag inklusive de delar som är under rådgiven förvaltning av Sambla.
        Vi uppdaterar informationen löpande så länge vi har tillgång till din
        fullmakt.
      </Text>
      <Paper px={"lg"} py={"md"} radius={"lg"} mb={"xl"}>
        {data && <PensionOverviewStack data={data} />}
      </Paper>

      {data && (
        <PolicyRecommendations
          fundSwitches={toFundSwitchPolicies(data)}
          transfers={transferInsurances.filter(
            (insurance: Insurance) =>
              insurance.insuranceIdentifier !== undefined,
          )}
          managed={managedInsurances}
          others={otherInsurances}
          adviceAge={data.parameters?.zAge}
          transfersFooter={
            managedInsurances.length > 0 ? (
              <ManagedAllocationCard data={data} />
            ) : null
          }
          fundSwitchesFooter={
            <FundSwitchAllocationCard allocation={data.fundSwitchAllocation} />
          }
        />
      )}
      <ContactUsPaper />
    </>
  );
}
